// src/pages/WatermarkPage.js
import React, { useEffect, useRef, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import KakaoAdFit from "../components/KakaoAdFit"; 
import "./WatermarkPage.css";

function WatermarkPage() {
  const canvasRef = useRef(null);
  const imgObjRef = useRef(null);
  const objectUrlRef = useRef(null);

  // 숨겨진 파일 입력 참조
  const fileInputRef = useRef(null);

  // 워터마크 텍스트 입력 참조
  const inputRef = useRef(null);

  const [darkMode, setDarkMode] = useState(false);

  // CSR 기준 canonical URL
  const canonicalUrl = useMemo(() => {
    if (typeof window !== "undefined") {
      return window.location.origin + window.location.pathname;
    }
    return null;
  }, []);

  // JSON-LD (SoftwareApplication)
  const jsonLd = useMemo(() => {
    const obj = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "워터마크 생성기",
      description:
        "이미지에 텍스트 워터마크를 단일/가로/세로/대각선 반복으로 적용하고 PNG로 저장하는 TooLoves 워터마크 생성기",
      applicationCategory: "UtilityApplication",
      operatingSystem: "Web",
      softwareVersion: "1.0.0",
      keywords:
        "워터마크, 이미지 워터마크, 텍스트 워터마크, 대각선 워터마크, 반복 워터마크, PNG 저장, TooLoves,무료",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "KRW",
      },
    };
    if (canonicalUrl) obj.url = canonicalUrl;
    return JSON.stringify(obj);
  }, [canonicalUrl]);

  // 다크/라이트 모드에 따라 네비게이션 바 색상 변경
  useEffect(() => {
    const metaThemeColor = document.querySelector("meta[name=theme-color]");
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", darkMode ? "#111827" : "#ffffff");
    } else {
      const newMeta = document.createElement("meta");
      newMeta.name = "theme-color";
      newMeta.content = darkMode ? "#111827" : "#ffffff";
      document.head.appendChild(newMeta);
    }
  }, [darkMode]);

  // 이미지/워터마크 상태
  const [imageLoaded, setImageLoaded] = useState(false);
  const [waterText, setWaterText] = useState("");
  const [fontSize, setFontSize] = useState(48);
  const [color, setColor] = useState("#111827");
  const [opacity, setOpacity] = useState(0.2); // 0.05~1
  const [orientation, setOrientation] = useState("horizontal"); // horizontal | vertical | diagonal
  const [repeatX, setRepeatX] = useState(false); // 가로 반복
  const [repeatY, setRepeatY] = useState(false); // 세로 반복
  const [gap, setGap] = useState(120); // 반복 간격(px)
  const [position, setPosition] = useState("center"); // 단일 배치 위치

  // 파일 버튼 클릭 -> 숨겨진 파일 input 클릭
  const onPickClick = () => {
    if (fileInputRef.current) {
      // 동일 파일 재선택 허용
      try {
        fileInputRef.current.value = "";
      } catch {}
      fileInputRef.current.click();
    }
  };

  // 파일 선택 시 이미지 로딩
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 이전 URL 정리
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }

    const objUrl = URL.createObjectURL(file);
    objectUrlRef.current = objUrl;

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imgObjRef.current = img;
      setImageLoaded(true);
      drawCanvas(); // 초기 1회 렌더
      URL.revokeObjectURL(objUrl); // 메모리 정리
      objectUrlRef.current = null;
    };
    img.src = objUrl;
  };

  // textarea 높이를 내용에 맞게 자동 조정
  const autoResizeTextarea = () => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  // 엔터키로 줄바꿈 입력 시 다음 프레임에서 높이 재계산
  const handleTextKeyDown = (e) => {
    if (e.key === "Enter") {
      requestAnimationFrame(() => autoResizeTextarea());
    }
  };

  // 붙여넣기 후 다음 프레임에서 높이 재계산
  const handleTextPaste = () => {
    requestAnimationFrame(() => autoResizeTextarea());
  };

  // 마운트/값 변경 시 자동 높이 보정
  useEffect(() => {
    autoResizeTextarea();
  }, [waterText]);

  useEffect(() => {
    // 초기 한 번 보정
    autoResizeTextarea();
  }, []);

  // 캔버스 렌더링
  const drawCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    const img = imgObjRef.current;
    if (!canvas || !ctx || !img) return;

    // 캔버스 크기를 원본 이미지 크기에 맞춤
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;

    // 배경 이미지 렌더
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    // 워터마크 텍스트 없으면 종료
    if (!waterText.trim()) return;

    // 텍스트 스타일
    const fontFamily =
      "Pretendard, Segoe UI, Arial, Apple SD Gothic Neo, Noto Sans KR, sans-serif";
    ctx.font = `${fontSize}px ${fontFamily}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    // 멀티라인 준비
    const lines = waterText.replace(/\r\n?/g, "\n").split("\n");
    const lineHeight = Math.round(fontSize * 1.3);

    // 블록 치수 계산
    let maxLineW = 1;
    for (const line of lines) {
      const w = Math.max(1, ctx.measureText(line).width);
      if (w > maxLineW) maxLineW = w;
    }
    const blockW = maxLineW;
    const blockH = Math.max(1, lines.length * lineHeight);

    // 방향별 회전 각도
    const angle =
      orientation === "vertical"
        ? -Math.PI / 2
        : orientation === "diagonal"
        ? -Math.PI / 4
        : 0;

    // 회전 크기 계산
    const cos = Math.cos(Math.abs(angle));
    const sin = Math.sin(Math.abs(angle));
    const rotW = angle !== 0 ? blockW * cos + blockH * sin : blockW;
    const rotH = angle !== 0 ? blockW * sin + blockH * cos : blockH;

    // 단일 그리기 함수
    const drawAt = (x, y) => {
      ctx.save();
      ctx.globalAlpha = Math.max(0.01, Math.min(1, opacity));
      ctx.fillStyle = color;
      const mid = (lines.length - 1) / 2;
      if (angle !== 0) {
        ctx.translate(x, y);
        ctx.rotate(angle);
        lines.forEach((line, i) => {
          const yOff = (i - mid) * lineHeight;
          ctx.fillText(line, 0, yOff);
        });
      } else {
        lines.forEach((line, i) => {
          const yOff = (i - mid) * lineHeight;
          ctx.fillText(line, x, y + yOff);
        });
      }
      ctx.restore();
    };

    // 반복 배치
    const stepX = rotW + gap;
    const stepY = rotH + gap;
    if (repeatX || repeatY) {
      const xs =
        repeatX && stepX > 0
          ? Array.from(
              { length: Math.ceil(canvas.width / stepX) + 2 },
              (_, i) => (i - 1) * stepX + stepX / 2
            )
          : [canvas.width / 2];
      const ys =
        repeatY && stepY > 0
          ? Array.from(
              { length: Math.ceil(canvas.height / stepY) + 2 },
              (_, i) => (i - 1) * stepY + stepY / 2
            )
          : [canvas.height / 2];

      for (const y of ys) {
        for (const x of xs) {
          drawAt(x, y);
        }
      }
      return;
    }

    // 단일 배치
    let x = canvas.width / 2;
    let y = canvas.height / 2;
    const pad = 24;
    if (position === "top-left") {
      x = pad + rotW / 2;
      y = pad + rotH / 2;
    } else if (position === "top-right") {
      x = canvas.width - pad - rotW / 2;
      y = pad + rotH / 2;
    } else if (position === "bottom-left") {
      x = pad + rotW / 2;
      y = canvas.height - pad - rotH / 2;
    } else if (position === "bottom-right") {
      x = canvas.width - pad - rotW / 2;
      y = canvas.height - pad - rotH / 2;
    }
    drawAt(x, y);
  };

  // 옵션 변경 시 재렌더
  useEffect(() => {
    drawCanvas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    imageLoaded,
    waterText,
    fontSize,
    color,
    opacity,
    orientation,
    repeatX,
    repeatY,
    gap,
    position,
  ]);

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");
    const a = document.createElement("a");
    a.href = pngUrl;
    a.download = "watermark.png";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleReset = () => {
    setWaterText("");
    setFontSize(48);
    setColor("#111827");
    setOpacity(0.2);
    setOrientation("horizontal");
    setRepeatX(false);
    setRepeatY(false);
    setGap(120);
    setPosition("center");
    setImageLoaded(false);
    imgObjRef.current = null;

    // 리셋 후 높이 보정
    requestAnimationFrame(() => autoResizeTextarea());
  };

  return (
    <div className={`App ${darkMode ? "dark" : "light"}`}>
      <Helmet htmlAttributes={{ lang: "ko" }}>
        <title>워터마크 생성기 | TooLoves</title>
        <meta
          name="description"
          content="이미지에 텍스트 워터마크를 단일/가로/세로/대각선 반복으로 적용하고 PNG로 저장할 수 있는 워터마크 생성기"
        />
        <meta
          name="keywords"
          content="워터마크, 이미지 워터마크, 텍스트 워터마크, 대각선 워터마크, 반복 워터마크, PNG 저장, TooLoves"
        />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <meta name="application-name" content="TooLoves" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* 초기 theme-color, 런타임에서 useEffect로 동기화 */}
        <meta name="theme-color" content={darkMode ? "#111827" : "#ffffff"} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="ko_KR" />
        <meta property="og:site_name" content="TooLoves" />
        <meta property="og:title" content="워터마크 생성기 | TooLoves" />
        <meta
          property="og:description"
          content="이미지에 텍스트 워터마크를 손쉽게 적용하고 PNG로 저장"
        />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta property="og:image" content="/og-watermark.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="워터마크 생성기 | TooLoves" />
        <meta
          name="twitter:description"
          content="이미지에 텍스트 워터마크를 손쉽게 적용하고 PNG로 저장"
        />
        <meta name="twitter:image" content="/og-watermark.png" />

        {/* Canonical */}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

        {/* JSON-LD */}
        <script type="application/ld+json">{jsonLd}</script>
      </Helmet>

      {/* 상단 앱바 */}
      <header className="app-bar">
        <div className="app-bar__inner">
          <Link to="/" className="brand-link" aria-label="TooLoves 홈으로 이동">
            TooLoves
          </Link>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button className="mode-toggle" onClick={() => setDarkMode(!darkMode)}>
              {darkMode ? "라이트 모드" : "다크 모드"}
            </button>
          </div>
        </div>
      </header>

      {/* 본문 */}
      <main className="container">
        {/* ✅ 기존 광고 위치: 제거됨 */}
        
        <section className="generator-card">
          <h2 className="card-title">이미지 워터마크</h2>
          <p className="card-subtitle">
            이미지를 올리고 문구를 입력하면 반투명 워터마크를 단일 또는 가로/세로/대각선 반복으로 적용하고
            PNG로 저장할 수 있습니다.
          </p>

          <form className="form" onSubmit={(e) => e.preventDefault()}>
            {/* ... form 내용 ... */}
            <div className="input-row">
              <input
                ref={fileInputRef}
                id="wm-file-input"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              <button
                type="button"
                onClick={onPickClick}
                aria-label="이미지 파일 선택"
                className="btn btn-primary"
              >
                파일 선택
              </button>
            </div>

            <div className="input-row" style={{ marginTop: 8 }}>
              <textarea
                ref={inputRef}
                className="text-input text-inputtextarea"
                placeholder="워터마크 문구를 입력"
                value={waterText}
                onChange={(e) => {
                  setWaterText(e.target.value);
                  autoResizeTextarea();
                }}
                onKeyDown={handleTextKeyDown}
                onPaste={handleTextPaste}
                rows={1}
              />
            </div>

            <div className="options-row">
              <div className="field">
                <label className="form-label">폰트 크기</label>
                <input
                  type="range"
                  min="16"
                  max="160"
                  step="2"
                  value={fontSize}
                  onChange={(e) => setFontSize(Number(e.target.value))}
                />
                <div className="hint">{fontSize}px</div>
              </div>

              <div className="field">
                <label className="form-label">문구 색상</label>
                <input type="color" value={color} onChange={(e) => setColor(e.target.value)} />
              </div>

              <div className="field">
                <label className="form-label">불투명도</label>
                <input
                  type="range"
                  min="0.05"
                  max="1"
                  step="0.05"
                  value={opacity}
                  onChange={(e) => setOpacity(Number(e.target.value))}
                />
                <div className="hint">{Math.round(opacity * 100)}%</div>
              </div>

              <div className="field">
                <label className="form-label">글자 방향</label>
                <select value={orientation} onChange={(e) => setOrientation(e.target.value)}>
                  <option value="horizontal">가로</option>
                  <option value="vertical">세로</option>
                  <option value="diagonal">대각선</option>
                </select>
              </div>

              <div className="field switch">
                <label className="form-label">가로 반복</label>
                <input
                  type="checkbox"
                  checked={repeatX}
                  onChange={(e) => setRepeatX(e.target.checked)}
                />
              </div>

              <div className="field switch">
                <label className="form-label">세로 반복</label>
                <input
                  type="checkbox"
                  checked={repeatY}
                  onChange={(e) => setRepeatY(e.target.checked)}
                />
              </div>

              <div className="field">
                <label className="form-label">반복 간격</label>
                <input
                  type="range"
                  min="16"
                  max="400"
                  step="4"
                  value={gap}
                  onChange={(e) => setGap(Number(e.target.value))}
                  disabled={!(repeatX || repeatY)}
                />
                <div className="hint">{gap}px</div>
              </div>

              <div className="field">
                <label className="form-label">단일 배치 위치</label>
                <select
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  disabled={repeatX || repeatY}
                >
                  <option value="center">가운데</option>
                  <option value="top-left">좌상단</option>
                  <option value="top-right">우상단</option>
                  <option value="bottom-left">좌하단</option>
                  <option value="bottom-right">우하단</option>
                </select>
              </div>
            </div>
          </form>

          {/* 미리보기 */}
          <div className="preview-card">
            {imageLoaded ? (
              <>
                <div className="canvas-wrap">
                  <canvas ref={canvasRef} id="wm-canvas" />
                </div>
                <div className="actions">
                  <button onClick={handleDownload}>PNG 다운로드</button>
                  <button className="btn-secondary" onClick={handleReset}>
                    초기화
                  </button>
                </div>
              </>
            ) : (
              <p className="placeholder">이미지를 선택하고 워터마크 문구를 입력하세요.</p>
            )}
          </div>
        </section>
      </main>

      <footer className="main-footer">
        <span>© 2025 TooLoves. All rights reserved.</span>
        <nav className="footer-links" aria-label="법적 고지 링크">
          <Link to="/terms" className="footer-link">이용약관</Link>
          <span className="footer-sep" aria-hidden="true">·</span>
          <Link to="/privacy" className="footer-link">개인정보처리방침</Link>
        </nav>
      </footer>

      {/* ✅ 광고: 푸터 밑으로 이동 */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', paddingBottom: '20px' }}>
        <KakaoAdFit />
      </div>
    </div>
  );
}

export default WatermarkPage;